import React, { Component } from 'react';
import get from 'lodash/get';
import * as analytics from 'shared/lib/analytics';
import PropTypes from 'prop-types';
import MomentPropTypes from 'react-moment-proptypes';
import PaymentLayout from 'shared/lib/components/PaymentLayout';
import LineItemsList from 'shared/lib/components/LineItemsList';
import SectionWrapper from 'shared/lib/components/SectionWrapper';
import currencyFormatter from 'currency-formatter';
import moment from 'moment-timezone';

const propTypes = {
  isCollectiblesEndorsementInProgress: PropTypes.bool,
  quote: PropTypes.shape({
    action: PropTypes.oneOf(['charge', 'refund', 'no_change']).isRequired,
    cart: PropTypes.shape({
      currency_code: PropTypes.string,
      items: PropTypes.array,
      payment_frequency: PropTypes.string,
      totals: PropTypes.shape({
        subtotal: PropTypes.number,
        grandtotal: PropTypes.number,
      }),
    }).isRequired,
    new_annual_total_premium: PropTypes.number,
    previous_annual_total_premium: PropTypes.number,
  }).isRequired,
  navTo: PropTypes.func,
  responses: PropTypes.object,
  policy: PropTypes.shape({
    details: PropTypes.shape({
      deductible: PropTypes.number,
    }).isRequired,
    coverage_start_date: MomentPropTypes.momentString,
    coverage_end_date: MomentPropTypes.momentString,
  }).isRequired,
  policyProvider: PropTypes.string,
  propertyAddress: PropTypes.object,
  submitPurchase: PropTypes.func,
};

const defaultProps = {
  isCollectiblesEndorsementInProgress: false,
};

class CollectiblesEndorsementPaymentView extends Component {
  componentDidMount() {
    analytics.page('Collectibles - Endorsement Payment', {
      categoryCode: this.props.categoryCode,
    });
    analytics.track('Checkout Step Viewed', {
      category: 'order',
      step: 1,
    });
  }

  handleSubmit = elements => {
    analytics.track('Checkout Step Completed', {
      category: 'order',
      step: 1,
    });

    const { submitPurchase, navTo, params, onError } = this.props;

    return submitPurchase({
      cardElement: get(elements, 'cardElement'),
      categoryCode: params.categoryCode,
      policyId: params.policyId,
    })
      .then(() => {
        navTo(
          `/account/policies/collectibles/${params.policyId}/${
            params.endorsementAction
          }/${params.categoryCode}/confirmation`
        );
      })
      .catch(onError);
  };

  formatPrice = price => {
    const { quote } = this.props;
    return currencyFormatter.format(price, {
      code: get(quote, 'cart.currency_code'),
    });
  };

  get coverageEndDate() {
    const endDate = get(this.props.policy, 'coverage_end_date');
    return moment(endDate, 'YYYY-MM-DD').format('MMM DD, YYYY');
  }

  get title() {
    switch (get(this.props, 'quote.action')) {
      case 'charge':
        return 'Your New Quote';
      case 'refund':
        return 'Your Reduced Quote';
      default:
        return 'Your Quote';
    }
  }

  get notice() {
    const { categoryConfig, params, quote } = this.props;
    const quoteAction = get(quote, 'action');

    if (params.endorsementAction === 'add') {
      return `A ${get(
        categoryConfig,
        'title'
      )} COLLECTION WILL BE ADDED TO YOUR EXISTING POLICY.`;
    } else if (quoteAction === 'no_change') {
      return 'YOUR QUOTE REMAINS UNCHANGED';
    } else if (quoteAction === 'refund') {
      return 'YOUR CHANGES WILL BE APPLIED TO YOUR EXISTING POLICY.';
    } else {
      return null;
    }
  }

  get billingRows() {
    const { quote } = this.props;

    switch (get(this.props.quote, 'action')) {
      case 'refund':
        return [
          [
            <strong>Refund amount</strong>,
            <strong>
              {this.formatPrice(
                get(quote, 'refund_details.total_refund_amount'),
                { code: get(quote, 'refund_details.currency_code') }
              )}
            </strong>,
          ],
        ];
      default:
        return [
          [
            <strong>Balance due</strong>,
            <strong>
              {this.formatPrice(get(quote, 'cart.totals.grandtotal'), {
                code: get(quote, 'cart.currency_code'),
              })}
            </strong>,
          ],
        ];
    }
  }

  render() {
    const {
      categoryConfig,
      disclaimer,
      isCollectiblesEndorsementInProgress,
      policy,
      policyProvider,
      propertyAddress,
      quote,
      responses,
    } = this.props;
    const quoteAction = get(quote, 'action');

    return (
      <div className="CollectiblesEndorsementPaymentView Container Container--small">
        <PaymentLayout
          title={this.title}
          notice={this.notice}
          policyDocUrl={get(this.props.policy, 'documents[0].url')}
          icon="widgets"
          billingCycle="year"
          isPurchaseInProgress={isCollectiblesEndorsementInProgress}
          pricePerCycle={get(quote, 'new_annual_total_premium')}
          totals={get(quote, 'cart.totals')}
          disclaimer={disclaimer}
          hideBillingSummary
          action={quoteAction}
          refundDetails={get(quote, 'refund_details')}
          onSubmit={this.handleSubmit}
        >
          <SectionWrapper title="Coverage details">
            <LineItemsList
              rows={[
                [
                  quoteAction === 'no_change'
                    ? 'Current premium'
                    : 'Previous premium',
                  `${this.formatPrice(
                    get(quote, 'previous_annual_total_premium')
                  )}/year`,
                ],
                ['Underwritten by', policyProvider],
                [
                  'Deductible',
                  this.formatPrice(get(policy, 'details.deductible')),
                ],
                [
                  'Coverage term',
                  `${moment().format('MMM DD, YYYY')} - ${
                    this.coverageEndDate
                  }`,
                ],
              ]}
            />
          </SectionWrapper>

          <SectionWrapper
            title={`${get(categoryConfig, 'title')} collection details`}
          >
            <LineItemsList
              rows={[
                [
                  'Location',
                  <div>
                    {propertyAddress.streetAddress}
                    {propertyAddress.unit && (
                      <span>
                        {propertyAddress.unit}
                        <br />
                      </span>
                    )}
                    <br />
                    {propertyAddress.city}, {propertyAddress.region}{' '}
                    {propertyAddress.postal}
                  </div>,
                ],
                [
                  'Highest valued item',
                  `${this.formatPrice(responses.highest_value_item)}`,
                ],
                [
                  'Total value',
                  `${this.formatPrice(responses.collection_total_value)}`,
                ],
              ]}
            />
          </SectionWrapper>

          {quoteAction !== 'no_change' && (
            <SectionWrapper title="Billing details">
              <LineItemsList
                rows={[
                  [
                    <strong>
                      {quoteAction === 'refund'
                        ? 'Refund amount'
                        : 'Balance due'}
                    </strong>,
                    <strong>
                      {this.formatPrice(
                        Math.abs(get(quote, 'cart.totals.grandtotal'))
                      )}
                    </strong>,
                  ],
                ]}
              />
            </SectionWrapper>
          )}
        </PaymentLayout>
      </div>
    );
  }
}

CollectiblesEndorsementPaymentView.propTypes = propTypes;
CollectiblesEndorsementPaymentView.defaultProps = defaultProps;

export default CollectiblesEndorsementPaymentView;
